import React, { useRef } from 'react';
import Modal from '../base/modal';
import AppButton from '../base/button';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import Invoice from '../base/invoice';
import StatusBadge from '../base/status-badge';
import BaseLabelText from '../base/label-text';
import { formatAmount } from '../../utils/amount.util';
import Loader from '../base/loader';
import { ITransaction } from '../../models/transaction.model';
import { getCurrencySign } from '../../utils/string.util';

interface TransactionDetailsProps {
  isOpen: boolean;
  toggleModal: () => void;
  selectedTransaction: ITransaction | null;
}

const TransactionDetails: React.FC<TransactionDetailsProps> = ({
  isOpen,
  toggleModal,
  selectedTransaction,
}) => {
  const invoiceRef = useRef<HTMLDivElement>(null); // Reference for both modal and invoice

  const downloadInvoice = async () => {
    if (!invoiceRef.current) return;

    // Temporarily make invoice visible
    invoiceRef.current.style.position = 'static';
    invoiceRef.current.style.opacity = '1';

    await new Promise((resolve) => setTimeout(resolve, 500)); // Allow rendering

    const canvas = await html2canvas(invoiceRef.current, {
      scale: window.devicePixelRatio || 2, // Higher scale for better quality
      useCORS: true,
      logging: false,
      scrollX: 0,
      scrollY: -window.scrollY, // Prevents viewport clipping
    });

    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF('p', 'mm', 'a4');
    const imgWidth = 210;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
    pdf.save(`invoice_${selectedTransaction?.transactionRef}.pdf`);

    // Hide invoice again after download
    invoiceRef.current.style.position = 'absolute';
    invoiceRef.current.style.opacity = '0';
  };
  return (
    <>
      <Modal isOpen={isOpen} toggleModal={toggleModal} width="w-[600px] h-fit">
        {/* Wrap both modal and Invoice in a single div */}
        <div className="bg-white rounded-xl">
          <h2 className="text-xl font-bold mb-8">Transaction Details</h2>

          {selectedTransaction ? (
            <div className="flex flex-col gap-6 rounded-xl border border-[#F1F2F4] px-6 pt-6">
              <div className="flex justify-between items-center border-b pb-6">
                <div>
                  <p className="font-bold text-xl">
                    {selectedTransaction?.beneficiaryDetail?.accountType}
                  </p>
                  <p className="text-sm text-gray-500">
                    Reference: {selectedTransaction?.transactionRef}
                  </p>
                </div>

                <StatusBadge
                  status={selectedTransaction.transactionStatus.toLowerCase()}
                />
              </div>

              <div className="border-b pb-6">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                  <BaseLabelText
                    label="Date"
                    value={new Date(
                      selectedTransaction.transactionDate,
                    ).toLocaleDateString('en-GB')}
                    type="vertical"
                  />
                  <BaseLabelText
                    label="Bank Name"
                    value={selectedTransaction?.beneficiaryDetail?.bankName}
                    type="vertical"
                  />
                  <BaseLabelText
                    label="Account Number"
                    value={
                      selectedTransaction?.beneficiaryDetail?.customerName
                    }
                    type="vertical"
                  />
                  <BaseLabelText
                    label="Sent Amount"
                    value={`${getCurrencySign(selectedTransaction.fromCurrencyCode)} ${formatAmount(selectedTransaction?.sentAmount)}`}
                    type="vertical"
                  />
                  <BaseLabelText
                    label="Expected Amount"
                    value={`${getCurrencySign(selectedTransaction.toCurrencyCode)} ${formatAmount(selectedTransaction?.expectedAmount)}`}
                    type="vertical"
                  />
                  <BaseLabelText
                    label="Fee"
                    value={`${getCurrencySign(selectedTransaction.fromCurrencyCode)} ${formatAmount(0)}`}
                    type="vertical"
                  />
                  <div className="col-span-2">
                    <BaseLabelText
                      label="Description"
                      value={`Lorem ipsum dolo ignant hello`}
                      type="vertical"
                    />
                  </div>
                </div>
              </div>

              <div className="pb-6">
                <AppButton label="Download Invoice" clicked={downloadInvoice} />
              </div>
            </div>
          ) : (
            <Loader
              width="1.2rem"
              borderTopColor="border-t-primary"
              className="shrink-0"
            />
          )}

          {/* Include Invoice inside the ref */}
        </div>
      </Modal>

      {/* Invoice container (initially hidden) */}
      <div
        ref={invoiceRef}
        style={{ position: 'absolute', left: '-9999999px', opacity: 0 }}
      >
        {selectedTransaction && <Invoice transaction={selectedTransaction} />}
      </div>
    </>
  );
};

export default TransactionDetails;
